<template>
    <div>
        <!-- Requests to move an approved WFH day within its week: approve by the day before the new day -->
        <v-card v-if="moves.length" outlined class="mb-4">
            <div class="subtitle-2 px-3 pt-2">
                <v-icon small color="blue" class="mr-1">mdi-calendar-arrow-right</v-icon>WFH day changes to approve ({{ moves.length }})
            </div>
            <div class="caption grey--text px-3">Approve by the day before the new day — on the day it's too late. HR is told once you approve.</div>
            <v-simple-table dense>
                <tbody>
                    <tr v-for="m in moves" :key="m.entry_id">
                        <td class="py-1">
                            <div class="font-weight-medium">{{ m.name }}</div>
                            <div class="caption grey--text">{{ m.empno }} · {{ m.section }}</div>
                        </td>
                        <td class="text-no-wrap">{{ fmtDay(m.from_date) }} → <strong>{{ fmtDay(m.to_date) }}</strong></td>
                        <td class="caption">{{ m.reason }}</td>
                        <td class="caption text-no-wrap" :class="m.approve_by === today ? 'orange--text text--darken-2' : 'grey--text'">
                            by {{ m.approve_by === today ? 'today' : fmtDay(m.approve_by) }}
                        </td>
                        <td class="text-no-wrap" width="1px">
                            <v-btn x-small text color="error" @click="openRejectMove(m)">Turn down</v-btn>
                            <v-btn x-small color="success" :loading="movingId === m.entry_id" @click="approveMove(m)">Approve</v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card>

        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px;">
            <v-btn-toggle v-model="filter" dense mandatory color="primary">
                <v-btn small value="approve">To approve ({{ count('approve') }})</v-btn>
                <v-btn small value="approved">Approved ({{ count('approved') }})</v-btn>
                <v-btn small value="unsent">Not sent ({{ count('unsent') }})</v-btn>
                <v-btn small value="all">All</v-btn>
            </v-btn-toggle>
            <v-select v-model="typeKey" :items="types" item-text="label" item-value="key"
                placeholder="All types" dense outlined hide-details clearable style="max-width: 200px;"></v-select>
            <v-text-field v-model="search" dense outlined hide-details clearable prepend-inner-icon="mdi-magnify"
                placeholder="Search name / section" style="max-width: 260px;"></v-text-field>
            <v-spacer></v-spacer>
            <span class="caption grey--text">Staff send their plans by {{ deadline && fmtFullDate(deadline) }}</span>
            <v-btn small color="success" :disabled="!approvable.length" :loading="approvingAll" @click="approveAll">
                <v-icon small left>mdi-check-all</v-icon>Approve all ({{ approvable.length }})
            </v-btn>
        </div>

        <v-card outlined :loading="loading">
            <v-simple-table dense>
                <thead>
                    <tr class="text-uppercase">
                        <th>Staff</th>
                        <th>Section</th>
                        <th>Plan</th>
                        <th>Dates</th>
                        <th>Status</th>
                        <th width="1px"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!loading && !shown.length">
                        <td colspan="6" class="text-center grey--text caption py-4">{{ emptyText }}</td>
                    </tr>
                    <tr v-for="r in shown" :key="r.key">
                        <td class="py-1">
                            <div class="font-weight-medium">{{ r.staff.name }}</div>
                            <div class="caption grey--text">{{ r.staff.empno }}</div>
                        </td>
                        <td class="caption">{{ r.staff.section }}<span v-if="r.staff.unit && r.staff.unit !== '-'"> / {{ r.staff.unit }}</span></td>
                        <td class="text-no-wrap">
                            <template v-if="r.plan">
                                <span class="type-dot" :style="{ background: r.plan.color }"></span>{{ r.plan.label }}
                            </template>
                            <span v-else class="caption grey--text">Nothing entered</span>
                        </td>
                        <td>
                            <template v-if="r.plan">
                                <v-chip v-for="e in r.plan.entries" :key="e.id" x-small label outlined class="mr-1 my-1" :color="r.plan.color">
                                    {{ r.plan.is_wfh ? fmtDay(e.from_date) : fmtRange(e) }}
                                </v-chip>
                            </template>
                        </td>
                        <td class="text-no-wrap">
                            <v-chip v-if="r.plan" x-small label :color="statusColor(r.plan.status)" text-color="white">
                                {{ statusText(r.plan.status) }}
                            </v-chip>
                            <div v-if="r.plan && r.plan.returned" class="caption orange--text text--darken-2 returned" :title="r.plan.returned.reason">
                                {{ r.plan.returned.reason }}
                            </div>
                        </td>
                        <td class="text-no-wrap">
                            <v-btn x-small text color="primary" @click="open(r.staff)">Open</v-btn>
                            <template v-if="r.plan">
                                <v-btn v-if="r.plan.permissions.can_return" x-small text color="error" @click="openReturn(r)">Send back</v-btn>
                                <v-btn v-if="r.plan.permissions.can_approve" x-small color="success" :loading="approving === r.key" @click="approve(r)">
                                    Approve
                                </v-btn>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-card>

        <v-dialog v-model="dialog.open" max-width="820px">
            <v-card v-if="dialog.staff">
                <v-card-title class="subtitle-1">
                    {{ dialog.staff.name }}
                    <span class="caption grey--text ml-2">{{ dialog.staff.section }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="dialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <month-schedule :year="year" :month="month" :staff-id="dialog.staff.staff_id" @changed="changed" />
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="rejectMoveDialog.open" max-width="420px">
            <v-card v-if="rejectMoveDialog.move">
                <v-card-title class="subtitle-2">Turn down {{ rejectMoveDialog.move.name }}'s WFH day change</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">
                        {{ fmtDay(rejectMoveDialog.move.from_date) }} → {{ fmtDay(rejectMoveDialog.move.to_date) }}. They are told the reason; the WFH day stays {{ fmtDay(rejectMoveDialog.move.from_date) }}.
                    </div>
                    <v-textarea v-model="rejectMoveDialog.reason" label="Reason (required)" outlined dense auto-grow rows="3" counter="1000"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="rejectMoveDialog.open = false">Cancel</v-btn>
                    <v-btn small color="error" :disabled="!rejectMoveDialog.reason.trim()" :loading="movingId === rejectMoveDialog.move.entry_id"
                        @click="confirmRejectMove">Turn down</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="returnDialog.open" max-width="420px">
            <v-card v-if="returnDialog.row">
                <v-card-title class="subtitle-2">Send back {{ returnDialog.row.staff.name }}'s {{ returnDialog.row.plan.label }} plan</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">They are told the reason, change the plan and send it again.</div>
                    <v-textarea v-model="returnDialog.reason" label="Reason (required)" outlined dense auto-grow rows="3" counter="1000"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="returnDialog.open = false">Cancel</v-btn>
                    <v-btn small color="error" :disabled="!returnDialog.reason.trim()" :loading="returning" @click="confirmReturn">Send back</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// The section head's approvals for a month (MonthlyScheduleService::team): one
// row per staff member and plan — WFH and annual leave are approved
// separately. What can be approved or sent back is the backend's call
// (each plan's `permissions`); "Open" shows the staff member's whole month.
// Above it, requests to move an approved WFH day within its week (any month).
import MonthSchedule from './MonthSchedule.vue'

const STATUS = {
    draft: { text: 'Not sent', color: 'grey' },
    returned: { text: 'Sent back', color: 'orange darken-1' },
    submitted: { text: 'Waiting for approval', color: 'blue' },
    approved: { text: 'Approved', color: 'success' },
}

export default {
    name: 'TeamReview',
    components: { MonthSchedule },
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true },
    },
    data: () => ({
        staff: [],
        deadline: null,
        loading: false,
        filter: 'approve',
        typeKey: null,
        search: '',
        approving: null,
        approvingAll: false,
        returning: false,
        dialog: { open: false, staff: null },
        returnDialog: { open: false, row: null, reason: '' },
        moves: [],
        movingId: null,
        rejectMoveDialog: { open: false, move: null, reason: '' },
    }),
    computed: {
        // One row per staff member and plan; staff with nothing entered get one empty row.
        rows() {
            const rows = []
            this.staff.forEach(s => {
                if (!s.plans.length) rows.push({ key: `${s.staff_id}`, staff: s, plan: null })
                s.plans.forEach(p => rows.push({ key: `${s.staff_id}:${p.type}`, staff: s, plan: p }))
            })
            return rows
        },
        types() {
            const byKey = {}
            this.staff.forEach(s => s.plans.forEach(p => { byKey[p.type] = { key: p.type, label: p.label } }))
            return Object.values(byKey)
        },
        // Rows matching the type and search filters (the status buttons count these).
        matching() {
            const q = (this.search || '').toLowerCase()
            return this.rows.filter(r =>
                (!this.typeKey || (r.plan && r.plan.type === this.typeKey))
                && (!q || `${r.staff.name} ${r.staff.empno} ${r.staff.section} ${r.staff.unit || ''}`.toLowerCase().includes(q)))
        },
        shown() {
            return this.matching.filter(r => this.inFilter(r, this.filter))
        },
        approvable() {
            return this.shown.filter(r => r.plan && r.plan.permissions.can_approve)
        },
        emptyText() {
            return this.filter === 'approve' ? 'Nothing waiting for your approval.' : 'No staff found.'
        },
        today() {
            const d = new Date()
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        },
    },
    watch: {
        year() { this.load() },
        month() { this.load() },
    },
    mounted() {
        this.load()
        this.loadMoves()
    },
    methods: {
        loadMoves() {
            this.$axios.get('/api/monthly-schedule/wfh-moves').then(resp => { this.moves = resp.data.requests })
        },
        approveMove(m) {
            this.movingId = m.entry_id
            this.$axios.post(`/api/monthly-schedule/staff/${m.staff_id}/entries/${m.entry_id}/move/approve`)
                .then(() => this.movesChanged())
                .finally(() => this.movingId = null)
        },
        openRejectMove(m) {
            this.rejectMoveDialog = { open: true, move: m, reason: '' }
        },
        confirmRejectMove() {
            const { move, reason } = this.rejectMoveDialog
            this.movingId = move.entry_id
            this.$axios.post(`/api/monthly-schedule/staff/${move.staff_id}/entries/${move.entry_id}/move/reject`, { reason: reason.trim() })
                .then(() => {
                    this.rejectMoveDialog.open = false
                    this.movesChanged()
                })
                .finally(() => this.movingId = null)
        },
        movesChanged() {
            this.loadMoves()
            this.changed()
        },
        load() {
            this.loading = true
            this.$axios.get('/api/monthly-schedule/team', { params: { year: this.year, month: this.month } })
                .then(resp => {
                    this.staff = resp.data.staff
                    this.deadline = resp.data.deadline
                })
                .finally(() => this.loading = false)
        },
        /** A plan changed here: reload the list and let the page redraw the calendar. */
        changed() {
            this.load()
            this.$emit('changed')
        },
        inFilter(r, filter) {
            if (filter === 'approve') return !!(r.plan && r.plan.permissions.can_approve)
            if (filter === 'approved') return !!(r.plan && r.plan.status === 'approved')
            if (filter === 'unsent') return !r.plan || ['draft', 'returned'].includes(r.plan.status)
            return true
        },
        count(filter) {
            return this.matching.filter(r => this.inFilter(r, filter)).length
        },
        statusText(status) {
            return (STATUS[status] || { text: status }).text
        },
        statusColor(status) {
            return (STATUS[status] || { color: 'grey' }).color
        },
        open(s) {
            this.dialog = { open: true, staff: s }
        },
        approve(r) {
            this.approving = r.key
            this.$axios.post(`/api/monthly-schedule/staff/${r.staff.staff_id}/approve`, { year: this.year, month: this.month, type: r.plan.type })
                .then(() => this.changed())
                .finally(() => this.approving = null)
        },
        async approveAll() {
            const plans = this.approvable.map(r => ({ staff_id: r.staff.staff_id, type: r.plan.type }))
            if (!plans.length) return
            const ok = await this.$confirm({
                title: `Approve ${plans.length} plan${plans.length === 1 ? '' : 's'}?`,
                text: 'After approval, only you or HR can change them.',
                icon: 'info',
                confirmText: 'Approve',
            })
            if (!ok) return
            this.approvingAll = true
            this.$axios.post('/api/monthly-schedule/approve-all', { year: this.year, month: this.month, plans })
                .then(() => this.changed())
                .finally(() => this.approvingAll = false)
        },
        openReturn(r) {
            this.returnDialog = { open: true, row: r, reason: '' }
        },
        confirmReturn() {
            const { row, reason } = this.returnDialog
            this.returning = true
            this.$axios.post(`/api/monthly-schedule/staff/${row.staff.staff_id}/return`,
                { year: this.year, month: this.month, type: row.plan.type, reason: reason.trim() })
                .then(() => {
                    this.returnDialog.open = false
                    this.changed()
                })
                .finally(() => this.returning = false)
        },
        fmtFullDate(s) {
            const [y, m, d] = s.split('-')
            return `${d}/${m}/${y}`
        },
        fmtDay(s) {
            const [, m, d] = s.split('-')
            const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${s}T00:00:00`).getDay()]
            return `${day} ${d}/${m}`
        },
        fmtRange(e) {
            const f = s => { const [, m, d] = s.split('-'); return `${d}/${m}` }
            const range = e.from_date === e.to_date ? f(e.from_date) : `${f(e.from_date)}–${f(e.to_date)}`
            return `${range} · ${e.days}d`
        },
    },
}
</script>

<style scoped>
.type-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
    vertical-align: middle;
}
.returned {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
